/**
 * eBay-style Hero Carousel
 *
 * Features:
 * - Auto-rotating slides with pause on hover
 * - Dot indicators
 * - Previous/Next arrows
 * - Promotional content with CTAs
 */

import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '../lib/utils';

interface HeroSlide {
  id: string;
  title: string;
  subtitle: string;
  ctaText: string;
  ctaLink: string;
  bgColor: string;
  textColor: string;
  image?: string;
}

const DEFAULT_SLIDES: HeroSlide[] = [
  {
    id: '1',
    title: "There's a deal for you, too",
    subtitle: "Don't miss a chance to save on items you've been looking for.",
    ctaText: 'Explore now',
    ctaLink: '/category/deals',
    bgColor: 'bg-gradient-to-r from-red-500 to-orange-400',
    textColor: 'text-white',
  },
  {
    id: '2',
    title: 'Live Auctions',
    subtitle: 'Bid in real-time on exclusive items from top sellers.',
    ctaText: 'Join Live',
    ctaLink: '/?format=live',
    bgColor: 'bg-gradient-to-r from-blue-600 to-purple-600',
    textColor: 'text-white',
  },
  {
    id: '3',
    title: 'Electronics & Tech',
    subtitle: 'Latest gadgets at unbeatable prices.',
    ctaText: 'Shop now',
    ctaLink: '/category/electronics',
    bgColor: 'bg-gradient-to-r from-pink-500 to-rose-500',
    textColor: 'text-white',
  },
  {
    id: '4',
    title: 'Start Selling Today',
    subtitle: 'Turn your items into cash. List for free!',
    ctaText: 'Start now',
    ctaLink: '/seller/products/new',
    bgColor: 'bg-gradient-to-r from-emerald-500 to-teal-500',
    textColor: 'text-white',
  },
];

interface HeroCarouselProps {
  slides?: HeroSlide[];
  autoPlayInterval?: number;
}

export function HeroCarousel({
  slides = DEFAULT_SLIDES,
  autoPlayInterval = 5000
}: HeroCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isHovered, setIsHovered] = useState(false);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);

  const goToSlide = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  // Auto-play functionality
  useEffect(() => {
    if (!isPlaying || isHovered) return;

    const timer = setInterval(goToNext, autoPlayInterval);
    return () => clearInterval(timer);
  }, [isPlaying, isHovered, goToNext, autoPlayInterval]);

  const currentSlide = slides[currentIndex];

  return (
    <div
      className="relative w-full overflow-hidden rounded-2xl"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Slide Content */}
      <div
        className={cn(
          'relative h-[280px] md:h-[340px] lg:h-[400px] transition-all duration-500',
          currentSlide.bgColor
        )}
      >
        <div className="absolute inset-0 flex items-center">
          <div className="container mx-auto px-8 md:px-12 lg:px-16">
            <div className="max-w-lg">
              <h2 className={cn(
                'text-3xl md:text-4xl lg:text-5xl font-bold mb-4',
                currentSlide.textColor
              )}>
                {currentSlide.title}
              </h2>
              <p className={cn(
                'text-lg md:text-xl mb-6 opacity-90',
                currentSlide.textColor
              )}>
                {currentSlide.subtitle}
              </p>
              <Link
                to={currentSlide.ctaLink}
                className="inline-flex items-center px-6 py-3 bg-white text-gray-900 font-semibold rounded-full hover:bg-gray-100 transition-colors shadow-lg"
              >
                {currentSlide.ctaText}
              </Link>
            </div>
          </div>

          {/* Optional Image */}
          {currentSlide.image && (
            <div className="hidden md:block absolute right-0 top-0 h-full w-1/2">
              <img
                src={currentSlide.image}
                alt=""
                className="h-full w-full object-cover object-center"
              />
            </div>
          )}
        </div>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={goToPrevious}
        className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all opacity-0 group-hover:opacity-100 hover:scale-110"
        style={{ opacity: isHovered ? 1 : 0 }}
        aria-label="Previous slide"
      >
        <ChevronLeft className="w-6 h-6 text-gray-700" />
      </button>

      <button
        onClick={goToNext}
        className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all opacity-0 group-hover:opacity-100 hover:scale-110"
        style={{ opacity: isHovered ? 1 : 0 }}
        aria-label="Next slide"
      >
        <ChevronRight className="w-6 h-6 text-gray-700" />
      </button>

      {/* Bottom Controls */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-4">
        {/* Dot Indicators */}
        <div className="flex items-center gap-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={cn(
                'w-2.5 h-2.5 rounded-full transition-all',
                index === currentIndex
                  ? 'bg-white scale-110'
                  : 'bg-white/50 hover:bg-white/75'
              )}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Play/Pause Button */}
        <button
          onClick={() => setIsPlaying(!isPlaying)}
          className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors"
          style={{ opacity: isHovered ? 1 : 0.5 }}
          aria-label={isPlaying ? 'Pause slideshow' : 'Play slideshow'}
        >
          {isPlaying ? (
            <Pause className="w-4 h-4 text-white" />
          ) : (
            <Play className="w-4 h-4 text-white" />
          )}
        </button>
      </div>
    </div>
  );
}
